import {
  GTChangeTVehicleCardsInput,
  GTChangeTVehicleDetailsInput,
  GTChangeTVehicleFeaturesInput,
  GTChangeTVehicleInsurancePolicyInput,
  GTChangeTVehicleLicensePlatesInput,
  GTChangeTVehicleRegistrationDetailsInput,
  GTDriverSuggestion,
  GTPartnerSuggestion,
  GTTVehicle,
  GTTVehicleLicensePlate,
  GTVehicleDocumentCategoryInput,
  GTVehicleRegistrationCategoryInput,
  GTVehicleSuggestion
} from 'gql/types.generated'
import {
  EBrandCoverage,
  EVehiclePlateType,
  IVehiclesFilters,
  IVehiclesV2FormInsuranceStepValues,
  IVehiclesV2FormStep1Values,
  IVehiclesV2FormStep2Values,
  IVehiclesV2FormValues
} from './types'
import { STATES } from 'utils/states'
import { isBoolean, isEmpty, trim } from 'lodash'
import { formatISODate, parseDate } from 'utils/date'
import { getQueryString } from 'utils/route'
import { filterEmptyValue } from 'utils/object'
import { TBooleanStr, stringIsBoolean, stringToBoolean } from 'utils/boolean'
import { isStringInEnum } from 'utils/enum'
import { TVehiclesInput, TVehicleTemplateTypeInput, VehicleStatus, VehicleStatusInput } from 'gql/generated/graphql'

const mapResponseToForm = (queryResponse: GTTVehicle): IVehiclesV2FormValues => {
  const formValues = {
    id: queryResponse.id,
    name: queryResponse.name,
    vin: queryResponse.vin,
    externalID: queryResponse.externalID,
    cityName: queryResponse.cityName,
    state: queryResponse.stateCode ? STATES.find((state) => state.code === queryResponse.stateCode) : null,
    odometerReading: queryResponse.odometerReading || null,
    companies: queryResponse.companies.map((company) => ({
      id: company.id,
      name: company.name
    })),
    partner: {
      id: queryResponse.partner.id,
      name: queryResponse.partner.name
    },
    template: {
      id: queryResponse.template.id,
      type: queryResponse.template.type,
      capacity: queryResponse.template.capacity
    },
    tagCards: queryResponse.cards.tagCards || [],
    fuelCards: queryResponse.cards.fuelCards || [],
    features: queryResponse.features
      ? {
          brandCoverage: queryResponse.features.brandCoverage as EBrandCoverage,
          make: queryResponse.features.make,
          model: queryResponse.features.model,
          year: queryResponse.features.year,
          amenities: queryResponse.features.amenities
        }
      : {
          brandCoverage: '' as EBrandCoverage | null,
          make: null,
          model: null,
          year: null,
          amenities: []
        },
    licensePlates: !isEmpty(queryResponse.licensePlates)
      ? (queryResponse.licensePlates as GTTVehicleLicensePlate[]).map((licensePlate) => ({
          type: licensePlate.type as EVehiclePlateType,
          number: licensePlate.number,
          state: licensePlate.stateCode
            ? {
                name: STATES.find((state) => state.code === licensePlate.stateCode)?.name || '',
                code: licensePlate.stateCode
              }
            : null
        }))
      : [
          {
            type: '' as EVehiclePlateType,
            number: '',
            state: null
          }
        ],
    drivers: queryResponse.drivers.map((driver) => ({
      id: driver.id as string,
      name: driver.name as string
    })),
    insurancePolicy: {
      expirationDate: queryResponse.insurancePolicy?.expirationDate
        ? parseDate(queryResponse.insurancePolicy?.expirationDate)
        : ('' as Date | ''),
      document: {
        name: queryResponse.insurancePolicy?.document?.name || '',
        file: '' as const,
        path: queryResponse.insurancePolicy?.document?.path || ''
      }
    },
    registration: {
      isPermanent: queryResponse.registrationDetails?.isPermanent || false,
      expirationDate: queryResponse.registrationDetails?.expirationDate
        ? parseDate(queryResponse.registrationDetails?.expirationDate)
        : ('' as Date | ''),
      state: queryResponse.registrationDetails?.stateCode
        ? {
            name: STATES.find((state) => state.code === queryResponse.registrationDetails?.stateCode)?.name || '',
            code: queryResponse.registrationDetails?.stateCode
          }
        : null,
      category: (queryResponse.registrationDetails?.category as unknown as GTVehicleRegistrationCategoryInput) || '',
      document: {
        name: queryResponse.registrationDetails?.document?.name || '',
        file: '' as const,
        path: queryResponse.registrationDetails?.document?.path || ''
      }
    },
    documents: queryResponse.documents.map((document) => ({
      category: document.category as unknown as GTVehicleDocumentCategoryInput,
      name: document.document.name,
      file: '' as const,
      path: document.document.path
    }))
  }
  return formValues
}

const mapVehicleDetailsFormToMutation = (formValues: IVehiclesV2FormStep1Values): GTChangeTVehicleDetailsInput => {
  return {
    name: trim(formValues.name),
    vin: formValues.vin,
    cityName: formValues.cityName || null,
    stateCode: formValues.state?.code || null,
    externalID: formValues.externalID ? trim(formValues.externalID) : null,
    companyIDs: formValues.companies.map(({ id }) => id)
  }
}

const mapVehicleCardsFormToMutation = (formValues: IVehiclesV2FormStep1Values): GTChangeTVehicleCardsInput => {
  return {
    fuelCards: formValues.fuelCards,
    tagCards: formValues.tagCards
  }
}

const mapVehicleFeaturesFormToMutation = (formValues?: IVehiclesV2FormStep2Values): GTChangeTVehicleFeaturesInput => {
  return {
    brandCoverage: formValues?.features.brandCoverage || null,
    amenities: formValues?.features.amenities || [],
    make: formValues?.features.make || null,
    model: formValues?.features.model || null,
    year: formValues?.features.year || null
  }
}

const mapVehicleLicensePlatesFormToMutation = (
  formValues?: IVehiclesV2FormStep2Values
): GTChangeTVehicleLicensePlatesInput => {
  return {
    licensePlates:
      formValues?.licensePlates.map((plate) => ({
        type: plate.type,
        number: plate.number,
        stateCode: plate.state?.code || null
      })) || []
  }
}

const mapVehicleInsurancePolicyFormToMutation = (
  formValues: IVehiclesV2FormInsuranceStepValues
): GTChangeTVehicleInsurancePolicyInput => {
  return {
    expirationDate: formValues.insurancePolicy.expirationDate
      ? formatISODate(formValues.insurancePolicy.expirationDate as Date)
      : '',
    filePath: formValues.insurancePolicy.document.path || null
  }
}

const mapVehicleRegistrationFormToMutation = (
  formValues: IVehiclesV2FormInsuranceStepValues
): GTChangeTVehicleRegistrationDetailsInput | null => {
  if (!formValues.registration.category) return null
  const { isPermanent } = formValues.registration

  return {
    isPermanent: isPermanent,
    expirationDate:
      formValues.registration.expirationDate && !isPermanent
        ? formatISODate(formValues.registration.expirationDate as Date)
        : null,
    stateCode: formValues.registration.state?.code || null,
    category: formValues.registration.category,
    filePath: formValues.registration.document?.path || null
  }
}

export const mapFiltersToSearchQueryString = (filters: IVehiclesFilters, pageNb = 1): string => {
  return getQueryString({
    page: pageNb,
    make: filters.make,
    vin: filters.vin,
    plate: filters.plate,
    externalId: filters.externalId,
    vehicleId: filters.vehicle.id,
    driverId: filters.driver.id,
    partnerId: filters.partner.id,
    isAssigned: isBoolean(filters.isAssigned) ? String(filters.isAssigned) : '',
    hasDriver: isBoolean(filters.hasDriver) ? String(filters.hasDriver) : '',
    status: filters.status || '',
    type: filters.type || '',
    plateType: filters.plateType || '',
    registrationStateCode: filters.registrationState ? filters.registrationState.code : ''
  })
}

export const VEHICLE_STATUS_LIST: VehicleStatus[] = [
  VehicleStatus.Active,
  VehicleStatus.Inactive,
  VehicleStatus.OutOfService,
  VehicleStatus.Banned
]
export const mapSearchToFiltersValues = (
  searchQueries: Record<string, string>,
  {
    vehicles,
    drivers,
    partners
  }: { vehicles: GTVehicleSuggestion[]; drivers: GTDriverSuggestion[]; partners: GTPartnerSuggestion[] }
): IVehiclesFilters => {
  const driver = drivers.find((driver) => driver.id === searchQueries.driverId)
  const vehicle = vehicles.find((vehicle) => vehicle.id === searchQueries.vehicleId)
  return {
    make: searchQueries.make || '',
    vin: searchQueries.vin || '',
    plate: searchQueries.plate || '',
    externalId: searchQueries.externalId || '',
    vehicle: {
      id: vehicle?.id || '',
      name: vehicle?.name || '',
      externalID: vehicle?.externalID || ''
    },
    driver: {
      id: driver?.id || '',
      name: driver?.name || ''
    },
    partner: partners.find((partner) => partner.id === searchQueries.partnerId) || {
      id: '',
      name: ''
    },
    isAssigned: stringIsBoolean(searchQueries.isAssigned)
      ? stringToBoolean(searchQueries.isAssigned as TBooleanStr)
      : null,
    hasDriver: stringIsBoolean(searchQueries.hasDriver)
      ? stringToBoolean(searchQueries.hasDriver as TBooleanStr)
      : null,
    status:
      searchQueries.status && VEHICLE_STATUS_LIST.includes(searchQueries.status as VehicleStatus)
        ? (searchQueries.status as VehicleStatus)
        : null,
    type: isStringInEnum(searchQueries.type, TVehicleTemplateTypeInput)
      ? (searchQueries.type as TVehicleTemplateTypeInput)
      : null,
    plateType: isStringInEnum(searchQueries.plateType, EVehiclePlateType)
      ? (searchQueries.plateType as EVehiclePlateType)
      : null,
    registrationState: STATES.find((state) => state.code === searchQueries.registrationStateCode) || null
  }
}

export const mapSearchQueryToInput = (searchQueries: Record<string, string>): TVehiclesInput => {
  return filterEmptyValue({
    make: searchQueries.make || null,
    vin: searchQueries.vin || null,
    plates: searchQueries.plate || null,
    id: searchQueries.vehicleId || null,
    driverID: searchQueries.driverId || null,
    partnerID: searchQueries.partnerId || null,
    externalID: searchQueries.externalId || null,
    isAssigned: stringIsBoolean(searchQueries.isAssigned)
      ? stringToBoolean(searchQueries.isAssigned as TBooleanStr)
      : null,
    hasDriver: stringIsBoolean(searchQueries.hasDriver)
      ? stringToBoolean(searchQueries.hasDriver as TBooleanStr)
      : null,
    status:
      searchQueries.status && VEHICLE_STATUS_LIST.includes(searchQueries.status as VehicleStatus)
        ? (searchQueries.status as VehicleStatusInput)
        : null,
    type: isStringInEnum(searchQueries.type, TVehicleTemplateTypeInput)
      ? (searchQueries.type as TVehicleTemplateTypeInput)
      : null,
    plateType: isStringInEnum(searchQueries.plateType, EVehiclePlateType)
      ? (searchQueries.plateType as EVehiclePlateType)
      : null,
    stateCode: searchQueries.registrationStateCode || null
  })
}

export const getVehicleLabelWithExternalId = (vehicle: Pick<GTVehicleSuggestion, 'name' | 'externalID'>): string => {
  let label = vehicle.name
  if (vehicle.externalID) {
    label += ` (${vehicle.externalID})`
  }

  return label
}

export const VehiclesV2FormUtils = {
  mapResponseToForm,
  mapVehicleDetailsFormToMutation,
  mapVehicleCardsFormToMutation,
  mapVehicleFeaturesFormToMutation,
  mapVehicleLicensePlatesFormToMutation,
  mapVehicleInsurancePolicyFormToMutation,
  mapVehicleRegistrationFormToMutation
}
